import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CSSModule from 'react-css-modules';
import { Search, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BusinessInfoTile, Spinner, SeoHead } from 'components';
import { actions } from 'coupon-common/src/modules/businesses/all';
import { SITE_URL } from 'components/SeoHead/SeoHead';

import { RouteNames } from '../../../constants';
import styles from './BusinessesListAll.module.scss';
// import { Helmet } from 'react-helmet';

const BusinessesListAll = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allState = useSelector(state => state.businesses.all);
  const { fetching, fetched, data, error } = allState;
  const { results: list, nextPage } = data || { results: [] };

  const [searchKey, setSearchKey] = useState('');

  const selectedCityName = useSelector(state => state.cities.activeCity) || 'your city';
  const selectedCityId = useSelector(state => state.cities.activeCityId);

  const getAllList = useCallback(
    params => {
      dispatch(actions.getAllList({ ...params, city: selectedCityId }));
    },
    [dispatch, selectedCityId],
  );

  useEffect(() => {
    getAllList();
  }, [getAllList]);

  const goToDetails = useCallback(id => navigate(`${RouteNames.BUSINESSES_DETAILS}/${id}`), [navigate]);

  const handleLoadMore = useCallback(() => {
    if (nextPage) {
      getAllList({ page: nextPage });
    }
  }, [nextPage, getAllList]);

  const filteredBusinesses = useMemo(() => {
    if (!list) return [];
    if (!searchKey.trim()) return list;
    const lowerSearchKey = searchKey.toLowerCase();
    return list.filter(business => business.name?.toLowerCase().includes(lowerSearchKey));
  }, [list, searchKey]);

  // ─── SEO ────────────────────────────────────────────────────────────
// ASSUMPTION: RouteNames.BUSINESSES_LIST_ALL is a guess at the constant
// name for this page's own route — confirm it matches what's actually
// defined in constants.js.
const businessListPath = RouteNames.BUSINESSES_LIST_ALL;
const hasSelectedCity = selectedCityName && selectedCityName !== 'your city';
const seoTitle = hasSelectedCity ? `Businesses in ${selectedCityName}` : 'All Businesses';
const seoDescription = `Browse local businesses offering deals and coupons${
  hasSelectedCity ? ` in ${selectedCityName}` : ' across Canada'
} on MyCoupon Book.`;
// Capped at 50 — this is only the current fetched page (list is paginated
// via nextPage/handleLoadMore), so it's a partial-but-valid ItemList, not
// the full business catalog.
const structuredData =
  list && list.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: list.slice(0, 50).map((business, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: business.name,
          url: `${SITE_URL}${RouteNames.BUSINESSES_DETAILS}/${business.id}`,
        })),
      }
    : undefined;

  return (
    <div styleName="page">
     <SeoHead
       title={seoTitle}
       description={seoDescription}
       path={businessListPath}
       type="website"
       structuredData={structuredData}
     />
      <div styleName="wrap">
        {selectedCityName && (
          <p styleName="city-notice">
            You are seeing businesses in <span styleName="city-name">{selectedCityName}</span>.{' '}
            <button type="button" styleName="city-change-link" onClick={() => navigate(RouteNames.LOCATION_SEARCH)}>
              Click here to search businesses in another city
            </button>
          </p>
        )}

        <div styleName="search-bar-wrap">
          <Search size={18} styleName="search-icon" />
          <input
            type="text"
            placeholder="Type the business name"
            value={searchKey}
            onChange={e => setSearchKey(e.target.value)}
            styleName="search-input"
          />
          {searchKey.length > 0 && (
            <button type="button" styleName="clear-btn" onClick={() => setSearchKey('')} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </div>

        {error && <p styleName="state-message">Something went wrong loading businesses.</p>}

        {!error && !fetched && fetching && (
          <div styleName="state-message">
            <Spinner show size="md" />
          </div>
        )}

        {!error && (!list || list.length === 0) && fetched && <p styleName="state-message">No businesses found.</p>}

        {!error && filteredBusinesses.length > 0 && (
          <>
            <div styleName="list">
              {filteredBusinesses.map((item, index) => (
                <BusinessInfoTile key={item.id} first={index === 0} data={item} onPress={() => goToDetails(item.id)} />
              ))}
            </div>
            {nextPage && (
              <div styleName="load-more-row">
                <button type="button" styleName="load-more-btn" onClick={handleLoadMore} disabled={fetching}>
                  {fetching ? <Spinner show size="sm" /> : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CSSModule(BusinessesListAll, styles, { allowMultiple: true });
